import {
  DatabaseAuto,
  IAnalyticsResultExpense,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdeleteOneResponse,
  Iexpense, IfilterProps,
  TbackEndGeneratedFields,
  TbackEndGeneratedFieldsExcludingId, validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { Imanager, ImanagerExpense } from '../../../shared-types';
import { ManagerClient } from '../../utils/manager-client';


/**
 * Type representing a manager expense.
 */
type TmanagerExpenseProps = ImanagerExpense<number, Imanager>;

export type TmanagerExpenseRequest<
CreateMode extends boolean = false, U = Omit<Partial<ImanagerExpense>,
  TbackEndGeneratedFieldsExcludingId | 'manager'>> = CreateMode extends true ? Omit<
  ImanagerExpense, TbackEndGeneratedFields | 'manager'> : U;


/**
 * Class representing a manager expense.
 */
export class ManagerExpense
  extends DatabaseAuto {
  /**
   * The base URL for the manager expense.
   */
  private static readonly baseUrl = '/expense';

  /**
   * The unique reference ID.
   */
  readonly urId: TmanagerExpenseProps['urId'];

  /**
   * The name of the expense.
   */
  readonly name: TmanagerExpenseProps['name'];

  /**
   * The manager of the expense.
   */
  readonly manager: TmanagerExpenseProps['manager'];

  /**
   * The cost of the expense.
   */
  readonly cost: TmanagerExpenseProps['cost'];

  /**
   * The category of the expense.
   */
  readonly category: TmanagerExpenseProps['category'];

  /**
   * The note of the expense.
   */
  readonly note: string;

  /**
   * The expense type.
   */
  readonly expenseType: Iexpense['expenseType'];


  /**
   * Constructs an instance of `ManagerExpense`.
   *
   * @param {TmanagerExpenseProps} data - The expense data used to initialize the instance.
   * @throws {Error} Throws an error if no data is provided.
   */
  constructor(data: TmanagerExpenseProps) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      },
      name: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'ManagerExpense name must be a non-empty string'
      },
      manager: {
        validate: (v) =>
          typeof v === 'object' &&
          v._id.trim().length > 0,
        message: 'Added by name must be a valid user object'
      },
      cost: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0,
        message: 'Cost must be a non-negative number'
      }
    });

    super(data);

    this.urId = data.urId;
    this.name = data.name;
    this.manager = data.manager;
    this.cost = data.cost;
    this.category = data.category;
    this.note = data.note || '';
    this.expenseType = data.expenseType;
  }

  /**
   * Retrieves expense analytics based on the provided filter.
   *
   * @param {IfilterProps} filter - The filter criteria for retrieving expense analytics.
   * @returns {Promise<IAnalyticsResultExpense>} The result of the analytics operation.
   */
  static getExpenseAnalytics(filter: IfilterProps) {
    const observer$ = ManagerClient.ehttp
      .makePost<IAnalyticsResultExpense>(`${ManagerExpense.baseUrl}/analytics`, filter);

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves all expenses with pagination support.
   *
   * @param {number} [offset=0] - The starting point for pagination.
   * @param {number} [limit=20] - The number of items to retrieve.
   * @returns {Promise<{ count: number, dataArr: ManagerExpense[] }>} An object containing
   * the total count and an array of `ManagerExpense` instances.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = ManagerClient.ehttp
      .makeGet<IdataArrayResponse<TmanagerExpenseProps>>(`${ManagerExpense.baseUrl}?${query}`);
    const expenses = await lastValueFrom(observer$);

    return {
      count: expenses.count,
      dataArr: expenses.data.map((val) => new ManagerExpense(val))
    };
  }

  /**
   * Filters expenses based on the provided filter properties.
   *
   * @param {IfilterProps} filter - The filter criteria for retrieving expenses.
   * @returns {Promise<{ count: number, dataArr: ManagerExpense[] }>} An object containing
   * the total count and an array of `ManagerExpense` instances.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = ManagerClient.ehttp
      .makePost<IdataArrayResponse<TmanagerExpenseProps>>(`${ManagerExpense.baseUrl}/filter`, filter);
    const expenses = await lastValueFrom(observer$);

    return {
      count: expenses.count,
      dataArr: expenses.data.map((val) => new ManagerExpense(val))
    };
  }

  /**
   * Retrieves a single expense based on the provided resource ID.
   *
   * @param {string} resourceId - The unique identifier for the expense to retrieve.
   * @returns {Promise<ManagerExpense>} The `ManagerExpense` instance corresponding to the provided resource ID.
   */
  static async getOne(resourceId: string) {
    const observer$ = ManagerClient.ehttp
      .makeGet<TmanagerExpenseProps>(`${ManagerExpense.baseUrl}/${resourceId}`);
    const expense = await lastValueFrom(observer$);

    return new ManagerExpense(expense);
  }

  /**
   * Adds a new expense.
   *
   * @param {TmanagerExpenseRequest<true>} payload - The expense data to add.
   * @returns {Promise<ManagerExpense>} The result of the addition operation.
   */
  static async add(payload: TmanagerExpenseRequest<true>) {
    const observer$ = ManagerClient.ehttp
      .makePost<TmanagerExpenseProps>(`${ManagerExpense.baseUrl}`, payload);

    return new ManagerExpense(await lastValueFrom(observer$));
  }

  /**
   * Removes multiple expenses based on the provided criteria.
   *
   * @param {IbulkDelete} payload - The criteria for deleting multiple expenses.
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the deletion operation.
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = ManagerClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${ManagerExpense.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current expense with the provided data.
   *
   * @param {TmanagerExpenseRequest} payload - The updated expense data.
   * @returns {Promise<ManagerExpense>} The result of the update operation.
   */
  async update(payload: TmanagerExpenseRequest) {
    const observer$ = ManagerClient.ehttp
      .makePut<TmanagerExpenseProps>(`${ManagerExpense.baseUrl}/${this._id}`, payload);

    return new ManagerExpense(await lastValueFrom(observer$));
  }

  /**
   * Removes the current expense.
   *
   * @returns {Promise<IdeleteOneResponse | null>} The result of the removal operation.
   */
  remove() {
    const observer$ = ManagerClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${ManagerExpense.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
